import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, DatePicker, Descriptions, Empty, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ReloadOutlined } from "@ant-design/icons";
import InfoAyuda, { AyudaBloque } from "../../../Components/InfoAyuda";
import { useFiltroPersistido, useRangoPersistido } from "../../../hooks/useFiltroPersistido";
import { botAsistenteApi } from "../../../services/botAsistente.api";
import type { BotEvento, BotEventoOrigen } from "../../../interfaces/botAsistente.interfaces";
import {
  AVISO_TEXTO_GUARDADO,
  MENUS_FILTRO,
  RESULTADOS_FILTRO,
  acortar,
  estiloTextoEtiqueta,
  etiquetaResultado,
  filasDelDetalle,
  formatoChile,
  formatoDuracion,
  resumenDeAgregado,
  textoCodigo,
  textoMenu,
  textoOrigen,
  textoRol,
  textoSubEstado,
  textoTipoMensaje,
} from "./botAsistente.utils";
import { expansorFila } from "./ExpansorFila";
import "./tablasPanel.css";

const { Text } = Typography;
const { RangePicker } = DatePicker;

type FiltroSimulado = "" | "true" | "false";
const ORIGENES: readonly BotEventoOrigen[] = ["whatsapp", "simulador"];
const SIMULADOS: readonly FiltroSimulado[] = ["", "true", "false"];
const MENU_MAX = 40;

export interface ActividadSeccionProps {
  /** Cambia cuando algo externo (por ejemplo el modo) obliga a volver a leer la lista. */
  version: number;
  /**
   * ¿Se está guardando el texto de los mensajes? (el ajuste «guardar el texto» que dice el servidor). `null` = no se sabe
   * (los ajustes no cargaron): el aviso no afirma ni una cosa ni la otra.
   */
  guardandoTexto?: boolean | null;
}

/** Texto del servidor que se muestra tal cual, pero acotado, o «—» si no hay. */
const textoOGuion = (v: unknown, max = 120): string => (typeof v === "string" && v !== "" ? acortar(v, max) : "—");

/** Una etiqueta de resultado con el contraste suficiente para leerse (el color solo no la define: lleva su texto). */
const EtiquetaDeResultado: React.FC<{ resultado: string; testId?: string }> = ({ resultado, testId }) => {
  const et = etiquetaResultado(resultado);
  return (
    <Tag color={et.color} style={{ margin: 0, ...estiloTextoEtiqueta(et.color) }} data-testid={testId}>
      {et.texto}
    </Tag>
  );
};

/** Qué significa cada resultado, a la vista (no en un tooltip, que en el celular no existe). */
const LeyendaResultados: React.FC = () => (
  <>
    {RESULTADOS_FILTRO.map((r) => {
      const et = etiquetaResultado(r);
      return (
        <div key={r} style={{ marginBottom: 4 }}>
          <EtiquetaDeResultado resultado={r} /> {et.ayuda}
        </div>
      );
    })}
  </>
);

const DetalleEvento: React.FC<{ e: BotEvento }> = ({ e }) => {
  const texto = typeof e.texto === "string" && e.texto !== "" ? e.texto : null;
  const significado = etiquetaResultado(e.resultado).ayuda;
  const filasDetalle = filasDelDetalle(e.detalle, e.simulado);
  return (
    <Descriptions size="small" bordered column={{ xs: 1, sm: 2 }} data-testid={`evento-detalle-${e.id}`}>
      {significado !== "" && (
        <Descriptions.Item label="Qué significa el resultado" span={2}>
          <span data-testid="evento-significado">{significado}</span>
        </Descriptions.Item>
      )}
      <Descriptions.Item label="Tipo de mensaje">{textoTipoMensaje(e.tipo_mensaje)}</Descriptions.Item>
      {/* El paso, el motivo y el rol salen con su nombre en español (nombresBitacora.ts); lo desconocido, tal cual entre paréntesis. */}
      <Descriptions.Item label="Paso de la conversación">
        <span data-testid="evento-paso">{acortar(textoSubEstado(e.sub_estado), 60)}</span>
      </Descriptions.Item>
      <Descriptions.Item label="Motivo">
        <span data-testid="evento-codigo">{acortar(textoCodigo(e.codigo), 160)}</span>
      </Descriptions.Item>
      {/* «Teléfono (oculto en parte)» y no «(enmascarado)»: a 360 px la palabra larga se partía a la mitad («enmascarad/o»). */}
      <Descriptions.Item label="Teléfono (oculto en parte)">{textoOGuion(e.telefono_mascara, 40)}</Descriptions.Item>
      <Descriptions.Item label="Usuario">
        {e.usuario_id != null ? `#${e.usuario_id}` : "—"}
        {e.rol ? ` · ${acortar(textoRol(String(e.rol)), 40)}` : ""}
      </Descriptions.Item>
      <Descriptions.Item label="Duración">{formatoDuracion(e.duracion_ms)}</Descriptions.Item>
      <Descriptions.Item label="Llamadas a la IA">{e.llamadas_ia ?? "—"}</Descriptions.Item>
      <Descriptions.Item label="Tokens (entrada / salida)">
        {e.tokens_entrada ?? "—"} / {e.tokens_salida ?? "—"}
      </Descriptions.Item>
      <Descriptions.Item label="¿Simulado?">{e.simulado ? "Sí (no se guardó nada)" : "No"}</Descriptions.Item>
      <Descriptions.Item label="Operación">{textoOGuion(e.operacion_id, 40)}</Descriptions.Item>
      {/* El diagnóstico del turno: solo las claves que el servidor deja pasar, con su nombre en español. */}
      {filasDetalle.map((f) => (
        <Descriptions.Item key={f.clave} label={f.etiqueta}>
          <span data-testid={`evento-dato-${f.clave}`} style={{ wordBreak: "break-word" }}>
            {acortar(f.valor, 120)}
          </span>
        </Descriptions.Item>
      ))}
      {texto !== null && (
        <Descriptions.Item label="Texto del mensaje" span={2}>
          {/* Texto libre de una persona: se muestra como texto (React lo escapa), respetando saltos y direcciones mixtas. */}
          <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", unicodeBidi: "plaintext" }} data-testid="evento-texto">
            {acortar(texto, 2000)}
          </span>
        </Descriptions.Item>
      )}
    </Descriptions>
  );
};

/**
 * (e) Actividad reciente: la bitácora de lo que le pasó a cada mensaje que llegó al asistente. Muestra lo esencial en
 * la fila y el resto al abrirla (tocar la fila entera, o su botón «Ver detalle»). Los filtros se recuerdan por usuario
 * (useFiltroPersistido, claves «asistenteWhatsapp.*»). El teléfono llega ya enmascarado desde el asistente y el texto del
 * mensaje no se guarda salvo que el superadministrador lo active: cuando SÍ se está guardando, un aviso lo dice arriba.
 *
 * Los menús y los códigos se muestran con su nombre en español (botAsistente.utils.ts); lo que no se conozca sale tal cual
 * entre paréntesis. Mientras carga, o si no se pudo leer, NO se afirma que «todavía no hay actividad».
 */
const ActividadSeccion: React.FC<ActividadSeccionProps> = ({ version, guardandoTexto = null }) => {
  const [rango, setRango] = useRangoPersistido("asistenteWhatsapp.rango", null);
  const [resultado, setResultado] = useFiltroPersistido<string>("asistenteWhatsapp.resultado", "", {
    validar: (v) => v === "" || RESULTADOS_FILTRO.some((r) => r === v),
  });
  const [origen, setOrigen] = useFiltroPersistido<string>("asistenteWhatsapp.origen", "", {
    validar: (v) => v === "" || ORIGENES.some((o) => o === v),
  });
  const [simulado, setSimulado] = useFiltroPersistido<FiltroSimulado>("asistenteWhatsapp.simulado", "", {
    validar: (v) => SIMULADOS.some((s) => s === v),
  });
  const [menu, setMenu] = useFiltroPersistido<string>("asistenteWhatsapp.menu", "", {
    // Solo un menú de la lista: el filtro era una caja de texto, y lo que alguien escribió ahí (o «cancelar», que ningún turno anota) se
    // vería como un valor crudo en el selector, sin una opción que lo explique.
    validar: (v) => v === "" || (typeof v === "string" && v.length <= MENU_MAX && MENUS_FILTRO.some((m) => m === v)),
  });

  const [filas, setFilas] = useState<BotEvento[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [tamano, setTamano] = useState(20);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const secuencia = useRef(0);

  // Los días elegidos viajan como YYYY-MM-DD: es el día que se marcó en el calendario, que el servidor lee en hora chilena.
  const desde = useMemo(() => rango?.[0]?.format("YYYY-MM-DD"), [rango]);
  const hasta = useMemo(() => rango?.[1]?.format("YYYY-MM-DD"), [rango]);

  const cargar = useCallback(
    async (p: number, s: number) => {
      const mia = ++secuencia.current;
      setCargando(true);
      const res = await botAsistenteApi.listarEventos({
        page: p,
        page_size: s,
        desde,
        hasta,
        resultado: resultado || undefined,
        origen: (origen || undefined) as BotEventoOrigen | undefined,
        simulado: simulado || undefined,
        menu: menu.trim() || undefined,
      });
      if (mia !== secuencia.current) return; // llegó tarde: ya se pidió otra cosa
      if (res === null) {
        setError(true);
      } else {
        setError(false);
        setFilas(res.eventos);
        setTotal(res.total);
        setPagina(res.page);
        setTamano(res.pageSize);
      }
      setCargando(false);
    },
    [desde, hasta, resultado, origen, simulado, menu],
  );

  useEffect(() => {
    void cargar(1, tamano);
    // `tamano` no se incluye a propósito: cambiar el tamaño de página ya llama a `cargar` desde la tabla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargar, version]);

  const hayFiltros = Boolean(rango || resultado || origen || simulado || menu);
  const limpiar = () => {
    setRango(null);
    setResultado("");
    setOrigen("");
    setSimulado("");
    setMenu("");
  };

  const columnas: ColumnsType<BotEvento> = [
    {
      // Fecha y origen apilados: así la fila principal cabe en 360 px sin deslizar (el resto está en la fila que se abre).
      title: "Fecha y origen",
      dataIndex: "creado_en",
      render: (iso: string, e) => (
        <div>
          <div style={{ whiteSpace: "nowrap" }}>{formatoChile(iso)}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {textoOrigen(e.origen)}
          </Text>
        </div>
      ),
    },
    {
      title: (
        // `wrap`: a 360 px el (i) baja a la línea de abajo en vez de ensanchar la columna (y la tabla) unos píxeles de más.
        <Space size={2} wrap>
          <span>Resultado y menú</span>
          <InfoAyuda titulo="Qué significa cada resultado" testId="ayuda-resultados">
            <LeyendaResultados />
          </InfoAyuda>
        </Space>
      ),
      dataIndex: "resultado",
      render: (r: string, e) => {
        const et = etiquetaResultado(r);
        return (
          <div>
            {/* El tooltip es un extra para quien tiene mouse; la explicación también está a la vista en el detalle y en el (i). */}
            <Tooltip title={et.ayuda || undefined}>
              <span>
                <EtiquetaDeResultado resultado={r} testId="evento-resultado" />
              </span>
            </Tooltip>
            <div style={{ wordBreak: "break-word" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {textoMenu(e.menu)}
              </Text>
            </div>
            {/* Una fila que el asistente agrupó vale por varios mensajes (los de gente que no es del equipo se anotan juntos): se dice a la vista. */}
            {resumenDeAgregado(e.detalle) !== null && (
              <div style={{ wordBreak: "break-word" }}>
                <Text style={{ fontSize: 12 }} data-testid="evento-agregado">
                  {resumenDeAgregado(e.detalle)}
                </Text>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Card
      size="small"
      data-testid="asistente-seccion-actividad"
      title={
        <Space size={4}>
          <span>Actividad reciente</span>
          <InfoAyuda titulo="Actividad reciente" testId="ayuda-actividad">
            <AyudaBloque titulo="¿Qué muestra?">
              Cada mensaje que le llegó al asistente y qué pasó con él. Toca una fila para ver el detalle.
            </AyudaBloque>
            <AyudaBloque titulo="Los resultados">
              <LeyendaResultados />
            </AyudaBloque>
            <AyudaBloque titulo="Privacidad">
              El teléfono se guarda enmascarado (solo se ven los últimos dígitos) y el texto de los mensajes no se guarda,
              salvo que el superadministrador lo active en «Modelos y límites».
            </AyudaBloque>
          </InfoAyuda>
        </Space>
      }
      extra={
        <Button size="small" icon={<ReloadOutlined />} loading={cargando} onClick={() => void cargar(pagina, tamano)} data-testid="actividad-actualizar">
          Actualizar
        </Button>
      }
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {guardandoTexto === true ? (
          <Alert
            type="warning"
            showIcon
            data-testid="actividad-aviso"
            message={AVISO_TEXTO_GUARDADO}
            description="El teléfono sigue viniendo enmascarado."
          />
        ) : (
          <Alert
            type="info"
            showIcon
            data-testid="actividad-aviso"
            message={
              guardandoTexto === false
                ? "El teléfono viene enmascarado y el texto del mensaje no se guarda."
                : "El teléfono viene enmascarado. No se pudo comprobar si se está guardando el texto de los mensajes."
            }
          />
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="actividad-filtros">
          <RangePicker
            style={{ flex: "1 1 240px", minWidth: 0, maxWidth: 320 }}
            value={rango}
            format="DD-MM-YYYY"
            placeholder={["Desde", "Hasta"]}
            onChange={(vals) => setRango(vals && vals[0] && vals[1] ? [vals[0], vals[1]] : null)}
            aria-label="Rango de fechas"
          />
          <Select
            allowClear
            placeholder="Resultado"
            style={{ flex: "1 1 140px", minWidth: 0, maxWidth: 200 }}
            value={resultado || undefined}
            onChange={(v) => setResultado(v ?? "")}
            options={RESULTADOS_FILTRO.map((r) => ({ value: r, label: etiquetaResultado(r).texto }))}
            aria-label="Filtrar por resultado"
          />
          <Select
            allowClear
            placeholder="Origen"
            style={{ flex: "1 1 130px", minWidth: 0, maxWidth: 180 }}
            value={origen || undefined}
            onChange={(v) => setOrigen(v ?? "")}
            options={ORIGENES.map((o) => ({ value: o, label: textoOrigen(o) }))}
            aria-label="Filtrar por origen"
          />
          <Select
            allowClear
            placeholder="Simulado o real"
            style={{ flex: "1 1 150px", minWidth: 0, maxWidth: 200 }}
            value={simulado || undefined}
            onChange={(v) => setSimulado((v ?? "") as FiltroSimulado)}
            options={[
              { value: "true", label: "Solo simulados" },
              { value: "false", label: "Solo reales" },
            ]}
            aria-label="Filtrar por simulado o real"
          />
          {/* Una lista con los menús que existen, con su nombre: antes era una caja de texto que solo filtraba al apretar Enter
              (y no lo decía) y pedía conocer el identificador interno («gasto»). Elegir uno filtra al instante. */}
          <Select
            allowClear
            placeholder="Menú"
            // Son pocas opciones: sin lista virtual (que dibuja solo las visibles y complica el teclado y los lectores de pantalla).
            virtual={false}
            style={{ flex: "1 1 180px", minWidth: 0, maxWidth: 240 }}
            value={menu || undefined}
            onChange={(v) => setMenu(v ?? "")}
            options={MENUS_FILTRO.map((m) => ({ value: m, label: textoMenu(m) }))}
            aria-label="Filtrar por menú"
          />
          <Button onClick={limpiar} disabled={!hayFiltros} data-testid="actividad-limpiar">
            Limpiar filtros
          </Button>
        </div>

        {error ? (
          // No se pudo leer: se dice eso y se ofrece reintentar. NO se dibuja la tabla, porque su texto de vacío («todavía no
          // hay actividad») afirmaría algo que no se sabe.
          <Alert
            type="error"
            showIcon
            data-testid="actividad-error"
            message="No se pudo cargar la actividad"
            description="Si el problema sigue, avisa a soporte."
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={() => void cargar(pagina, tamano)}>
                Reintentar
              </Button>
            }
          />
        ) : (
          <Table<BotEvento>
            // `tabla-asistente` (tablasPanel.css): sin el ancho mínimo de 800 px de las tablas del sistema y sin scroll
            // horizontal: a 360 px se ven la fecha, el resultado y el menú sin deslizar.
            className="tabla-asistente"
            rowKey="id"
            size="small"
            loading={cargando}
            dataSource={filas}
            columns={columnas}
            // La fila entera abre el detalle (expandRowByClick): el cursor de mano lo anuncia a quien tiene mouse.
            onRow={() => ({ style: { cursor: "pointer" } })}
            expandable={{
              expandRowByClick: true,
              expandIcon: expansorFila,
              columnWidth: 48,
              expandedRowRender: (e) => <DetalleEvento e={e} />,
            }}
            locale={{
              // Mientras carga no se dice nada: el texto de vacío se veía tenue debajo del indicador de carga.
              emptyText: cargando ? (
                <span />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={hayFiltros ? "No hay actividad con esos filtros." : "Todavía no hay actividad registrada."}
                />
              ),
            }}
            pagination={{
              current: pagina,
              pageSize: tamano,
              total,
              showSizeChanger: true,
              pageSizeOptions: [20, 50, 100],
              showTotal: (t) => `${t} ${t === 1 ? "mensaje" : "mensajes"}`,
              // Los DOS argumentos: ignorar el tamaño dejaría muerto el selector de «por página».
              onChange: (p, s) => void cargar(p, s),
            }}
          />
        )}
        <Text type="secondary" style={{ fontSize: 12 }}>
          Las fechas están en hora de Chile.
        </Text>
      </Space>
    </Card>
  );
};

export default ActividadSeccion;
